/**
 * useSplitResize — drag handle logic for the split pane divider.
 *
 * Returns mouse event handlers for a 4px divider bar. While dragging,
 * the split ratio updates in the workspace store. Clamped to 20%–80%
 * to prevent either pane from becoming unusably small.
 */
import { useCallback, useRef } from 'react';

import { useWorkspaceStore } from '../stores/useWorkspaceStore';

/** Drag handle for the split pane divider. */
export function useSplitResize(containerRef: React.RefObject<HTMLDivElement | null>) {
  const setSplitRatio = useWorkspaceStore(s => s.setSplitRatio);
  const dragging = useRef(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!dragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const ratio = (moveEvent.clientX - rect.left) / rect.width;
        setSplitRatio(ratio);
      };

      const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [containerRef, setSplitRatio]
  );

  return { onMouseDown };
}
